import { DocumentChartBarIcon } from '@heroicons/react/24/solid';
import { BarChart } from '@mui/x-charts';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type { SelectOption } from 'react-select-me';
import SelectMe from 'react-select-me';

import { formatDate, ShortMonths } from '../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { DPE, DPEDetail } from '../../../@types/DPE';
import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { coproTypes } from '../../../utils/copros';
import { DPEDict } from '../../../utils/dictionnayMap';
import { dpeClasses, getDpeColor } from '../../../utils/dpeColors';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import Spinner from '../../common/Spinner';
import DpeDetailsPopup from '../components/DpeDetailsPopup';

interface DpeKPI {
  [key: string]: number;  
}

interface CoproDpe {
  id: number;
  name: string;
  batchSize: number;
  type: number;
  validatedByUser: boolean;
  validatedByAdmin: boolean;
  remoteId: string;
  kpi: DpeKPI;
}

interface DateData {
  dateValue: string;
  [classe: string]: number | string; // Allow both numbers (for classes) and string (for month)
}

const headers = [
  { title: 'N° IMMAT' },
  { title: 'NOM' },
  { title: 'NB LOTS HAB / LOCAL PRO' },
  { title: 'TYPE' },
  { title: 'DPE (depuis 2021)' },
  { title: 'ACTIONS' },
];

const dataKeys = ['remoteId', 'name', 'batchSize'];

const detailHeaders = [
  { title: 'CLASSE' },
  { title: 'DATE RECEPTION' },
  { title: 'SURFACE (m²)' },
  { title: 'INFO COMPLEMENTAIRE' },
  { title: 'ACTIONS' },
];

interface YearOption extends SelectOption<string> {
  label: string;
  value: string;
}

function DpeVision() {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const location = useLocation(); // Initialize useLocation
  const { coproId } = location.state || {}; // Extract coproId from state
  const [data, setData] = useState<CoproDpe[]>([]);
  const [dpes, setDpes] = useState<DPEDetail[]>([]);
  const [dpesImmeuble, setDpesImmeuble] = useState<DPEDetail[]>([]);
  const [filteredDpes, setFilteredDpes] = useState<DPEDetail[]>([]);
  const [selectedYears, setSelectedYears] = useState<YearOption[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [detailStatus, setDetailStatus] = useState<AsyncStatus>('IDLE');
  const [selectedCopro, setSelectedCopro] = useState<CoproDpe | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isMonthlyView, setIsMonthlyView] = useState<boolean>(false);
  const [selectedDpeDetails, setSelectedDpeDetails] = useState<Partial<DPE>>({});
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);
  const navigate = useNavigate();

  // from 2021 to current year
  const yearsAvailable = Array.from({ length: new Date().getFullYear() - 2021 + 1 }, (_, index) => 2021 + index).map((year) => ({ label: year.toString(), value: year.toString() }));

  useEffect(() => {
    if (selectedYears) {
      const selectedYearValues = selectedYears.map((year) => year.value);
      setFilteredDpes(selectedYearValues.length > 0
        ? dpes.filter((item) => selectedYearValues.includes(new Date(item.dateEtablissementDpe).getFullYear().toString()))
        : dpes);
      setIsMonthlyView(selectedYearValues.length === 1);
    }
  }, [selectedYears, dpes]);

  const fetchCoprosDpesKpiData = () => {
    setStatus('PENDING');
    fetch(`/api/companies/${company.id}/copros/dpes/kpi`, JWTHeader(jwt))
      .then((response) => response.json())
      .then((response) => {
        setData(response);
        setStatus('SUCCESS');
      })
      .catch((error) => {
        console.error('Error fetching data :', error);
        setStatus('ERROR');
      });
  };

  const handleDpeDetailClick = (dpeDetails: DPEDetail) => {
    const orderedDpeDetails: Record<string, unknown> = {};
    Object.keys(DPEDict).forEach((key) => {
      if (dpeDetails[key]) {
        orderedDpeDetails[key] = dpeDetails[key];
      }
    });
    setSelectedDpeDetails(orderedDpeDetails);
    setIsModalOpen(true);
  };

  const handleDetailsClick = (copro: CoproDpe) => {
    if (selectedCopro) {
      setSelectedCopro(null);
      setDpes([]);
      setDpesImmeuble([]);
      setSelectedDpeDetails({});
      setFilteredDpes([]);
    } else {
      // Open the details view
      setSelectedCopro(copro);
      setDetailStatus('PENDING');
      fetch(`/api/copros/${copro.id}/dpes/history`, JWTHeader(jwt))
        .then((response) => response.json())
        .then((response) => {
          setDpes(response.filter((dpe) => dpe.typeBatiment !== 'immeuble'));
          setDpesImmeuble(response.filter((dpe) => dpe.typeBatiment === 'immeuble').sort((a, b) => new Date(b.dateReceptionDpe).getTime() - new Date(a.dateReceptionDpe).getTime()));
          // set all years from 2021 up to current years as selected
          setSelectedYears(yearsAvailable);
          setDetailStatus('SUCCESS');
        })
        .catch((error) => {
          console.error('Error fetching data :', error);
          setDetailStatus('ERROR');
        });
    }
  };

  useEffect(() => {
    if (company && company.id && jwt) {
      fetchCoprosDpesKpiData();
    }
  }, [company, jwt]);

  useEffect(() => {
    if (coproId && data.length > 0) {
      const selectedCoproFromNavigation = data.find((copro) => copro.id === coproId);
      if (selectedCoproFromNavigation) {
        handleDetailsClick(selectedCoproFromNavigation);
      }
    }
  }, [coproId, data]);

  const getDateDPECounts = () => {
    if (isMonthlyView) {
      const monthlyCounts = new Array(12).fill(0);
      filteredDpes.forEach((item) => {
        const date = new Date(item.dateEtablissementDpe);
        const month = date.getMonth();
        monthlyCounts[month] += 1;
      });
      return monthlyCounts.map((count, index) => ({
        label: new Date(0, index).toLocaleString('fr-FR', { month: 'short' }),
        value: count,
      }));
    }
    const yearlyCounts = {};
    filteredDpes.forEach((item) => {
      const year = new Date(item.dateEtablissementDpe).getFullYear().toString();
      yearlyCounts[year] = (yearlyCounts[year] || 0) + 1;
    });

    // Ensure all years from 2021 to the current year are included
    const allYears = Array.from({ length: new Date().getFullYear() - 2021 + 1 }, (_, index) => 2021 + index);
    return allYears.map((year) => ({
      label: year.toString(),
      value: yearlyCounts[year.toString()] || 0,
    }));
  };

  const getDateClasseBilanCounts = () => {
    const monthlyClasseCounts: DateData[] = ShortMonths.map((month) => {
      const monthData: DateData = { dateValue: month };
      dpeClasses.forEach((classe) => {
        monthData[classe] = 0; // Start with a count of 0 for each class
      });
      return monthData;
    });

    const yearlyClasseCounts: DateData[] = yearsAvailable.map((year) => {
      const yearData: DateData = { dateValue: year.value };
      dpeClasses.forEach((classe) => {
        yearData[classe] = 0; // Start with a count of 0 for each class
      });
      return yearData;
    });

    filteredDpes.forEach((item) => {
      const date = new Date(item.dateEtablissementDpe);
      const year = date.getFullYear().toString();
      const month = new Date(0, date.getMonth()).toLocaleString('fr-FR', { month: 'short' });
      const classe = item.classeBilanDpe;

      // Monthly aggregation
      const monthData = monthlyClasseCounts.find((m) => m.dateValue.toLowerCase() === month);
      if (monthData && typeof monthData[classe] === 'number') {
        monthData[classe] += 1; // Increment count for the specific classe
      }

      // Yearly aggregation
      const yearData = yearlyClasseCounts.find((y) => y.dateValue === year);
      if (yearData && typeof yearData[classe] === 'number') {
        yearData[classe] += 1; // Increment count for the specific classe
      }
    });

    return isMonthlyView
      ? dpeClasses.map((classe) => ({
        data: monthlyClasseCounts.map((monthData) => monthData[classe] as number),
        label: `Classe ${classe}`,
        color: getDpeColor(classe),
      }))
      : dpeClasses.map((classe) => ({
        data: yearlyClasseCounts.map((yearData) => yearData[classe] as number),
        label: `Classe ${classe}`,
        color: getDpeColor(classe),
      }));
  };

  const toggleFold = (index: number) => {
    if (openIndexes.includes(index)) {
      setOpenIndexes(openIndexes.filter((i) => i !== index));
    } else {
      setOpenIndexes([...openIndexes, index]);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <div className="mt-4 mb-12 text-lg">
          <p>
            Cette page vous permet de consulter l'intégralité des DPEs établis sur vos copropriétés.
            <br />
            Elle permet d'avoir rapidement une vue d'ensemble sur le nombre de DPEs établis, leur classe énergétique, ainsi que les dates auxquelles ils ont été établis.
            <br />
            Vous pouvez également consulter le détail complet de chacun des DPEs.
          </p>
        </div>
        {status === 'SUCCESS' && (
          <div className="my-10">
            <MUITable
              data={data.filter((copro) => (!selectedCopro || (selectedCopro && selectedCopro.id === copro.id))).map((copro) => {
                const isExpanded = selectedCopro && selectedCopro.id === copro.id;
                const item: Record<string, string | number | React.ReactNode> = {};
                dataKeys.forEach((key, index) => {
                  item[headers[index].title] = copro[key];
                });
                item.NOM = (
                  <div className="flex items-center">
                    <RedirectionCoproButtons coproId={copro.id} />
                    {copro.name}
                  </div>
                );
                item.TYPE = coproTypes[copro.type];
                item['DPE (depuis 2021)'] = (
                  <div>
                    {Object.entries(copro.kpi).map(([key, value]) => (
                      <span key={key} className="block">
                        <strong>
                          {key}
                          {' '}
                          :
                          {' '}
                        </strong>
                        {value}
                      </span>
                    ))}
                  </div>
                );
                const hasDpe = copro.kpi && Object.keys(copro.kpi).length > 0;
                item.ACTIONS = (
                  <button
                    className={classNames(
                      'w-full bg-rgpd-blue-dark hover:bg-rgpd-blue-dark-dark text-white text-base rounded shadow-md align-center whitespace-nowrap py-3 px-5 mr-2 flex items-center justify-center',
                      !hasDpe && 'opacity-80',
                    )}
                    onClick={() => handleDetailsClick(copro)}
                    key={copro.id}
                  >
                    {isExpanded ? 'Revenir à la liste' : (hasDpe ? 'Voir les DPEs' : 'Aucun DPE')}
                  </button>
                );
                return item;
              })}
              sortableColumns={['N° IMMAT', 'NOM', 'NB LOTS HAB / LOCAL PRO', 'TYPE']}
              headers={headers}
              tableClass="min-w-full bg-white border text-sm"
              isSearchable={!selectedCopro}
              disablePagination={!!selectedCopro}
            />
            {selectedCopro && (
              detailStatus === 'SUCCESS'
                ? (
                  <AnimatePresence>
                    <motion.div
                      initial={{ opacity: 1, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden rounded-b-[20px] border-x border-b p-4"
                    >
                      <h2 className="mb-4 text-xl font-bold">DPE de l'immeuble</h2>
                      {
                        !dpesImmeuble 
												|| ((dpesImmeuble.length === 0) && (
												  <div className="mx-10 my-10 text-center">
												    <Badge mode="warning" text="Aucun DPE Immeuble" />
												  </div>
												))
                      }
                      { dpesImmeuble && dpesImmeuble.length > 0
                      && (
                        <div>
                          <div className="mt-2 mb-4 rounded border border-gray-300 p-4">
                            <div className="md:grid md:grid-cols-2 md:gap-4">
                              <div className="mb-4 md:mb-0">
                                <p>
                                  Identifiant du DPE :{' '}
                                  <b>{dpesImmeuble[0].identifiantDpe}</b>
                                </p>
                                <p>
                                  Classe du DPE :{' '}
                                  <b>{dpesImmeuble[0].classeBilanDpe}</b>
                                </p>
                                <p>
                                  Date de réception :{' '}
                                  <b>{formatDate(dpesImmeuble[0].dateReceptionDpe)}</b>
                                </p>
                                <p>
                                  Surface habitable de l'immeuble (m²) :{' '}
                                  <b>{dpesImmeuble[0].surfaceHabitableImmeuble}</b>
                                </p>
                                {dpesImmeuble[0].complRefLogement && (
                                  <p>
                                    Info DPE :
                                    {' '}
                                    <b>{dpesImmeuble[0].complRefLogement}</b>
                                    <br />
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center justify-center">
                                <button
                                  className="align-center flex w-full items-center justify-center rounded border-2 border-rgpd-blue-dark bg-transparent px-5 py-3 text-base whitespace-nowrap text-rgpd-blue-dark shadow-md hover:bg-rgpd-blue-dark hover:text-white md:w-1/2"
                                  onClick={() => handleDpeDetailClick(dpesImmeuble[0])}
                                  key={dpesImmeuble[0].id}
                                >
                                  Voir le détail
                                </button>
                              </div>
                            </div>
                          </div>
                          {dpesImmeuble.slice(1).map((dpe, index) => (
                            <div key={dpe.id} className="mb-4">
                              <button
                                className="align-center flex w-full items-center justify-between rounded bg-gray-200 px-4 py-2 text-base whitespace-nowrap text-black shadow-md hover:bg-gray-300"
                                onClick={() => toggleFold(index)}
                              >
                                <span>
                                  DPE de l'immeuble effectué le{' '}
                                  {formatDate(dpesImmeuble[0].dateReceptionDpe)}
                                </span>
                                <span>{openIndexes.includes(index) ? '▲' : '▼'}</span>
                              </button>

                              {openIndexes.includes(index) && (
                                <div className="mt-2 rounded border border-gray-300 p-4">
                                  <div className="md:grid md:grid-cols-2 md:gap-4">
                                    <div className="mb-4 md:mb-0">
                                      <p>
                                        Identifiant du DPE :{' '}
                                        <b>{dpe.identifiantDpe}</b>
                                      </p>
                                      <p>
                                        Classe du DPE :{' '}
                                        <b>{dpe.classeBilanDpe}</b>
                                      </p>
                                      <p>
                                        Date de réception :{' '}
                                        <b>{formatDate(dpe.dateReceptionDpe)}</b>
                                      </p>
                                      <p>
                                        Surface habitable de l'immeuble (m²) :{' '}
                                        <b>{dpe.surfaceHabitableImmeuble}</b>
                                      </p>
                                      {dpe.complRefLogement && (
                                        <p>
                                          Info DPE :
                                          {' '}
                                          <b>{dpe.complRefLogement}</b>
                                          <br />
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex items-center justify-center">
                                      <button
                                        className="hover:bg-rgpd-blue-dark-dark align-center flex w-full items-center justify-center rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-1/2"
                                        onClick={() => handleDpeDetailClick(dpe)}
                                      >
                                        Voir le détail
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <h2 className="mt-8 mb-4 text-xl font-bold">Tous les DPEs de la copropriété</h2>
                      { dpes && dpes.length > 0 ? (
                        <>
                          <SelectMe
                            id="year-select"
                            name="years"
                            value={selectedYears}
                            onChange={(selectedOptions) => setSelectedYears(selectedOptions as YearOption[])}
                            options={yearsAvailable}
                            multiple
                            closeOnSelect={false}
                            listPosition="bottom"
                            placeholder="Sélectionnez une ou plusieurs années"
                            s={{ dd__selectedItem: '!bg-rgpd-white !border-rgpd-blue-light border-1 mx-2 text-rgpd-blue-light font-semibold' }}
                          />
                          <div className="grid grid-cols-1 md:grid-cols-2">
                            <BarChart
                              className="md:col-span-1"
                              dataset={getDateDPECounts()}
                              xAxis={[{
                                scaleType: 'band',
                                data: isMonthlyView
                                  ? ShortMonths
                                  : (selectedYears.length > 0 ? selectedYears.map((year) => year.value).sort((a, b) => parseInt(a, 10) - parseInt(b, 10)) : yearsAvailable.map((year) => year.value).sort((a, b) => parseInt(a, 10) - parseInt(b, 10))),
                                label: isMonthlyView ? 'Mois' : 'Année',
                              }]}
                              yAxis={[{ label: 'Nombre de DPEs', tickMinStep: 1 }]}
                              series={[
                                {
                                  dataKey: 'value',
                                },
                              ]}
                              height={400}
                            />
                            <BarChart
                              className="md:col-span-1"
                              xAxis={[{
                                scaleType: 'band',
                                data: isMonthlyView
                                  ? ShortMonths
                                  : (selectedYears.length > 0 ? selectedYears.map((year) => year.value).sort((a, b) => parseInt(a, 10) - parseInt(b, 10)) : yearsAvailable.map((year) => year.value).sort((a, b) => parseInt(a, 10) - parseInt(b, 10))),
                                label: isMonthlyView ? 'Mois' : 'Année',
                              }]}
                              yAxis={[{ label: 'Nombre de DPEs - Par classe', tickMinStep: 1 }]}
                              series={getDateClasseBilanCounts()}
                              height={400}
                            />
                          </div>
                          <div className="flex w-full items-center justify-center">
                            <button
                              className="mt-8 mb-8 flex w-full items-center justify-center rounded bg-rgpd-blue px-2 py-1 text-white md:w-auto"
                              onClick={() => navigate(ROUTES.PROSPECTING_GENERATE_DPE_REPORT, { state: { selectedCoproId: selectedCopro.id } })}
                            >
                              <DocumentChartBarIcon className="mr-2 inline h-5 w-5" />
                              Générer un rapport des DPEs
                            </button>
                          </div>
                          <MUITable
                            data={filteredDpes.map((dpe) => {
                              const item: Record<string, string | number | React.ReactNode> = {};
                              item.CLASSE = <div key={dpe.id} className="flex h-full items-center justify-center" style={{ backgroundColor: getDpeColor(dpe.classeBilanDpe) }}>{dpe.classeBilanDpe}</div>;
                              item['DATE RECEPTION'] = formatDate(dpe.dateReceptionDpe);
                              item['SURFACE (m²)'] = dpe.surfaceHabitableLogement && parseFloat(dpe.surfaceHabitableLogement).toFixed();
                              item['INFO COMPLEMENTAIRE'] = (
                                <>
                                  {dpe.complEtageAppartement != null && String(dpe.complEtageAppartement).trim() !== '' && (
                                    <>
                                      <b>Etage renseigné :</b>
                                      {' '}
                                      {dpe.complEtageAppartement}
                                      <br />
                                    </>
                                  )}
                                  {dpe.complRefLogement && (
                                    <>
                                      <b>Info DPE :</b>
                                      {' '}
                                      {dpe.complRefLogement}
                                      <br />
                                    </>
                                  )}
                                </>
                              );
                              item.ACTIONS = (
                                <button
                                  className="hover:bg-rgpd-blue-dark-dark align-center mr-2 flex w-full items-center justify-center rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md"
                                  onClick={() => handleDpeDetailClick(dpe)}
                                  key={dpe.id}
                                >
                                  Voir le détail
                                </button>
                              );
                              return item;
                            })}
                            sortableColumns={['CLASSE', 'DATE RECEPTION', 'SURFACE (m²)']}
                            headers={detailHeaders}
                            tableClass="min-w-full bg-white border text-sm"
                            disablePagination
                          />
                          {filteredDpes.length === 0 && (
                            <div className="my-6 flex items-center justify-center">
                              <Badge mode="warning" text="Aucun DPE trouvé pour ces filtres" />
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="mx-10 my-10 text-center">
                          <Badge mode="warning" text="Aucun DPE pour la copropriété" />
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                )
                : detailStatus === 'ERROR' ? (
                  <div className="mx-10 my-32 text-center">
                    <Badge mode="error" text="Une erreur est survenue lors du chargement des données." />
                  </div>
                )
                  : detailStatus === 'PENDING' && (
                    <div className="mx-10 my-32 text-center">
                      <Spinner aria-label="Center-aligned spinner" size="xl" />
                    </div>
                  )
            )}
          </div>
        )}
        {status === 'ERROR' && (
          <div className="mx-10 my-32 text-center">
            <Badge mode="error" text="Une erreur est survenue lors du chargement des données." />
          </div>
        )}
        {status === 'PENDING' && (
          <div className="mx-10 my-32 text-center">
            <Spinner aria-label="Center-aligned spinner" size="xl" />
          </div>
        )}
        <DpeDetailsPopup
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          dpeDetails={selectedDpeDetails}
        />
      </div>
    </div>
  );
}

export default DpeVision;
